import React, { useState, useEffect } from 'react';
import { useUsers } from '../../context/UsersContext';
import HealthCuesLogo from '../HealthCuesLogo';
import UserProfileDropdown from '../UserProfileDropdown';
import EnrollmentDecisionForm from './EnrollmentDecisionForm';
import Icon from '../Icon';
import { C } from '../../utils/styles';
import api from '../../utils/api';
import MyTrainingHome from '../learner/MyTrainingHome';
import MandatoryTrainingBanner from '../learner/MandatoryTrainingBanner';

export default function EmployeeDashboard({ gatingStatus }) {
  const { currentUser } = useUsers();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false);
  const [empTab, setEmpTab] = useState('dashboard'); // 'dashboard' | 'training'

  useEffect(() => {
    loadPortalData();
  }, []);

  const loadPortalData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employee/me`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPortalData(data);

        // Auto-show enrollment form if not yet decided
        const status = data.employee?.enrollmentStatus;
        if (status === 'invited' || status === 'opened' || status === 'in_progress') {
          setShowEnrollmentForm(true);
        }
      }
    } catch (error) {
      console.error('Failed to load portal data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'rgb(248, 250, 252)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 14, color: 'rgb(100, 116, 139)' }}>Loading...</div>
      </div>
    );
  }

  if (!portalData) {
    return (
      <div style={{ minHeight: '100vh', background: 'rgb(248, 250, 252)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Icon name="alert-circle-outline" style={{ fontSize: 48, color: C.orange, marginBottom: 16 }} />
          <div style={{ fontSize: 14, color: 'rgb(100, 116, 139)' }}>Unable to load your enrollment information.</div>
        </div>
      </div>
    );
  }

  const employee = portalData.employee || {};
  const client = portalData.client || {};
  const content = portalData.content || {};
  const savings = portalData.savings || {};
  const enrollmentStatus = employee.enrollmentStatus || 'invited';
  const hasDecided = enrollmentStatus === 'enrolled' || enrollmentStatus === 'declined';

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(248, 250, 252)', fontFamily: "'Inter', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid rgb(226, 232, 240)', padding: '16px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <HealthCuesLogo size={32} />
            <div style={{ fontSize: 14, fontWeight: 600, color: 'rgb(71, 85, 105)' }}>
              {client.name}
            </div>
            {/* Employee tab bar */}
            <div style={{ display: 'flex', gap: 4, marginLeft: 12, background: C.bg, borderRadius: 8, padding: 4 }}>
              {[
                { id: 'dashboard', label: 'Dashboard', icon: 'home-outline' },
                { id: 'training', label: 'Training', icon: 'school-outline' },
              ].map((t) => {
                const isActive = empTab === t.id;
                const pending = t.id === 'training' && gatingStatus?.blocked;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`employee-tab-${t.id}`}
                    onClick={() => setEmpTab(t.id)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 6,
                      border: 'none',
                      background: isActive ? '#fff' : 'transparent',
                      color: isActive ? C.accent : C.textMid,
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 12,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontFamily: "'Inter', sans-serif",
                      boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                    }}
                  >
                    <Icon name={t.icon} style={{ fontSize: 14 }} />
                    {t.label}
                    {pending && <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.orange }} />}
                  </button>
                );
              })}
            </div>
          </div>
          <UserProfileDropdown />
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 24px' }}>
        {empTab === 'training' ? (
          <MyTrainingHome embedded={true} />
        ) : showEnrollmentForm ? (
          <EnrollmentDecisionForm
            employee={employee}
            client={client}
            content={content}
            savings={savings}
            onComplete={(decision) => {
              setShowEnrollmentForm(false);
              loadPortalData();
            }}
            onCancel={() => setShowEnrollmentForm(false)}
          />
        ) : (
          <div>
            {/* Soft mandatory-training banner */}
            <MandatoryTrainingBanner onGoToTraining={() => setEmpTab('training')} />

            {/* Hero Section */}
            <div
              data-testid="employee-dashboard-hero"
              style={{
                background: 'linear-gradient(135deg, rgb(204, 251, 241) 0%, rgb(224, 242, 254) 100%)',
                borderRadius: 16,
                padding: '48px 40px',
                marginBottom: 32,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div className="noise-overlay" style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22120%22 height=%22120%22 filter=%22url(%23n)%22 opacity=%220.08%22/%3E%3C/svg%3E')",
                mixBlendMode: 'multiply'
              }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <h1 style={{ fontSize: 32, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", margin: 0, marginBottom: 8, color: 'rgb(15, 23, 42)' }}>
                  {content.hero?.headline || 'Welcome to Your Employee Benefits Enrollment'}
                </h1>
                <p style={{ fontSize: 16, color: 'rgb(71, 85, 105)', margin: 0, maxWidth: 700 }}>
                  {content.hero?.subheadline || 'Review your personalized tax savings and complete your enrollment in just a few minutes.'}
                </p>
              </div>
            </div>

            {/* Enrollment Status */}
            {hasDecided && (
              <div
                style={{
                  background: enrollmentStatus === 'enrolled' ? 'rgb(220, 252, 231)' : 'rgb(254, 243, 199)',
                  border: `1px solid ${enrollmentStatus === 'enrolled' ? 'rgb(187, 247, 208)' : 'rgb(253, 230, 138)'}`,
                  borderRadius: 12,
                  padding: 20,
                  marginBottom: 32,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16
                }}
              >
                <Icon
                  name={enrollmentStatus === 'enrolled' ? 'checkmark-circle' : 'information-circle-outline'}
                  style={{ fontSize: 32, color: enrollmentStatus === 'enrolled' ? 'rgb(22, 163, 74)' : 'rgb(217, 119, 6)' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: 'rgb(15, 23, 42)' }}>
                    {enrollmentStatus === 'enrolled' ? 'Enrollment Complete' : 'Decision Recorded'}
                  </div>
                  <div style={{ fontSize: 14, color: 'rgb(71, 85, 105)' }}>
                    {enrollmentStatus === 'enrolled'
                      ? 'Thank you for enrolling! Your tax savings will begin with your next payroll cycle.'
                      : 'You have declined enrollment. Your current payroll setup will remain unchanged.'}
                  </div>
                  {employee.enrollmentSubmittedAt && (
                    <div style={{ fontSize: 12, color: 'rgb(100, 116, 139)', marginTop: 8 }}>
                      Submitted on {new Date(employee.enrollmentSubmittedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Savings Summary */}
            <div
              data-testid="employee-savings-summary"
              style={{
                background: '#fff',
                border: '1px solid rgb(226, 232, 240)',
                borderRadius: 16,
                padding: 32,
                marginBottom: 32
              }}
            >
              <h2 style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", margin: 0, marginBottom: 24, color: 'rgb(15, 23, 42)' }}>
                Your Potential Tax Savings
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Annual Premium
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", color: 'rgb(15, 23, 42)', fontVariantNumeric: 'tabular-nums' }}>
                    ${savings.annualPremium?.toLocaleString() || '0'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Total Annual Savings
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", color: 'rgb(22, 163, 74)', fontVariantNumeric: 'tabular-nums' }}>
                    ${savings.totalAnnualSavings?.toLocaleString() || '0'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Monthly Savings
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", color: 'rgb(14, 116, 144)', fontVariantNumeric: 'tabular-nums' }}>
                    ${savings.monthlySavings?.toLocaleString() || '0'}
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits Grid */}
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", margin: 0, marginBottom: 20, color: 'rgb(15, 23, 42)' }}>
                How It Works
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                {(content.benefits || []).map((benefit, idx) => (
                  <div
                    key={benefit.id || idx}
                    data-testid={`employee-benefit-card-${benefit.id}`}
                    style={{
                      background: '#fff',
                      border: '1px solid rgb(226, 232, 240)',
                      borderRadius: 12,
                      padding: 20
                    }}
                  >
                    <Icon name={benefit.icon} style={{ fontSize: 28, color: 'rgb(14, 116, 144)', marginBottom: 12 }} />
                    <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: 'rgb(15, 23, 42)' }}>
                      {benefit.title}
                    </div>
                    <div style={{ fontSize: 13, color: 'rgb(71, 85, 105)', lineHeight: 1.5 }}>
                      {benefit.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", margin: 0, marginBottom: 20, color: 'rgb(15, 23, 42)' }}>
                Frequently Asked Questions
              </h2>
              <div
                data-testid="employee-faq-accordion"
                style={{ background: '#fff', border: '1px solid rgb(226, 232, 240)', borderRadius: 12, overflow: 'hidden' }}
              >
                {(content.faq || []).map((item, idx) => (
                  <details key={idx} style={{ borderBottom: idx < content.faq.length - 1 ? '1px solid rgb(226, 232, 240)' : 'none' }}>
                    <summary
                      style={{
                        padding: 20,
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'rgb(15, 23, 42)',
                        cursor: 'pointer',
                        listStyle: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      {item.question}
                      <Icon name="chevron-down-outline" style={{ fontSize: 18, color: 'rgb(100, 116, 139)' }} />
                    </summary>
                    <div style={{ padding: '0 20px 20px', fontSize: 14, color: 'rgb(71, 85, 105)', lineHeight: 1.6 }}>
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>

            {/* CTA */}
            {!hasDecided && (
              <div style={{ textAlign: 'center' }}>
                <button
                  data-testid="employee-review-enroll-button"
                  onClick={() => setShowEnrollmentForm(true)}
                  style={{
                    padding: '14px 32px',
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#fff',
                    background: 'rgb(14, 116, 144)',
                    border: 'none',
                    borderRadius: 10,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s, box-shadow 0.15s',
                    boxShadow: '0 1px 2px rgba(2, 6, 23, 0.06)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgb(11, 93, 115)';
                    e.target.style.boxShadow = '0 10px 30px rgba(2, 6, 23, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgb(14, 116, 144)';
                    e.target.style.boxShadow = '0 1px 2px rgba(2, 6, 23, 0.06)';
                  }}
                >
                  {content.ctaPrimary || 'Review & Enroll Now'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
